import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const selectedInputStyles = "border-primary-600 ring-1 ring-primary-600";

export const unselectedInputStyles = "border-gray-200";

export function getSelectionStyles(isSelected: boolean) {
  if (isSelected) {
    return "border-2 border-blue-600 bg-white";
  }
  return "";
}

export const getBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" | "open" | "closed" | "onhold" => {
  // Normalize status to uppercase for comparison
  const normalizedStatus = status.toUpperCase().trim();

  switch (normalizedStatus) {
    // Positive/Active statuses (open variant)
    case "PAID":
    case "APPROVED":
    case "ACCEPTED":
    case "ACTIVE":
    case "AVAILABLE":
      return "open";

    // Warning/Pending statuses (onhold variant)
    case "DUE SOON":
    case "DUE_SOON":
    case "PENDING":
    case "INITIATED":
    case "REVIEWED":
    case "SCHEDULED":
      return "onhold";

    // Negative/Urgent statuses (destructive variant)
    case "OVERDUE":
    case "OUT OF STOCK":
    case "OUT_OF_STOCK":
      return "destructive";

    // Inactive/Closed statuses (closed variant)
    case "CLOSED":
    case "CANCELLED":
    case "INACTIVE":
    case "DISABLED":
    case "REJECTED":
      return "closed";

    // Draft/Neutral statuses (outline variant)
    case "DRAFT":
      return "outline";

    default:
      return "default";
  }
};

export function camelCaseWordWithUnderscore(str: string): string {
  return str
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function convertArrayToSentence<T>({
  array,
  property,
}: {
  array: T[];
  property: keyof T;
}): string {
  const items = array.map((item) => item[property] as string);

  if (items.length === 1) {
    return items[0];
  } else if (items.length === 2) {
    return items.join(" and ");
  } else {
    return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
  }
}

export const getSourceLabel = (activitySubType: string) => {
  switch (activitySubType) {
    case "Return":
    case "Site Transfer":
    case "Lost":
      return "Site";
    case "Warehouse Transfer":
    case "Deliver":
    case "Dispose":
    case "Sell":
      return "Warehouse";
    case "Purchase":
      return "Vendor";
    default:
      return "Source";
  }
};

export const getDestinationLabel = (activitySubType: string) => {
  switch (activitySubType) {
    case "Return":
    case "Warehouse Transfer":
    case "Purchase":
      return "Warehouse";
    case "Dispose":
      return "Store";
    case "Deliver":
    case "Site Transfer":
      return "Site";
    case "Sell":
      return "Customer";
    default:
      return "Destitnation";
  }
};

export const getSources = (activitySubType: string, warehouses: any[] = [], sites: any[] = [], vendors: any[] = []) => {
  switch (activitySubType) {
    case "Return":
    case "Site Transfer":
      return sites.map(site => ({ label: site.name, value: site.id }));
    case "Warehouse Transfer":
    case "Deliver":
    case "Dispose":
    case "Sell":
      return warehouses.map(warehouse => ({ label: warehouse.name, value: warehouse.id }));
    case "Purchase":
      return vendors.map(vendor => ({ label: vendor.fullName, value: vendor.id }));
    default:
      return [];
  }
};

export const getDestinations = (activitySubType: string, warehouses: any[] = [], sites: any[] = [], customers: any[] = []) => {
  switch (activitySubType) {
    case "Return":
    case "Warehouse Transfer":
    case "Purchase":
      return warehouses.map(warehouse => ({ label: warehouse.name, value: warehouse.id }));
    case "Deliver":
    case "Site Transfer":
      return sites.map(site => ({ label: site.name, value: site.id }));
    case "Sell":
      return customers.map(customer => ({ label: customer.fullName, value: customer.id }));
    default:
      return [];
  }
};

export const getQuantityColumnName = (activitySubType: string) => {
  const sub = String(activitySubType ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");
  switch (sub) {
    case "warehouse transfer":
      return "Transfer Qty";
    case "deliver":
      return "Deliver Qty";
    case "dispose":
      return "Dispose Qty";
    case "sell":
      return "Sell Qty";
    case "purchase":
      return "Good Qty";
    case "return":
      return "Return Qty(Good)";
    case "site transfer":
      return "Transfer Qty";
    case "lost":
      return "Lost Qty";
    default:
      return "Transfer Qty";
  }
};

/** Minimal fields for site-centric movement Excel sign / header logic. */
export type MovementExcelSignInput = {
  sourceSiteId?: string | null;
  destinationSiteId?: string | null;
  activityType?: string;
  activitySubType?: string;
};

/** Movement location fields used to resolve source/destination display names. */
export type MovementExcelLocationInput = MovementExcelSignInput & {
  sourceSite?: { name?: string; customer?: { fullName?: string } } | null;
  sourceWarehouse?: { name?: string } | null;
  sourceVendor?: { fullName?: string } | null;
  destinationSite?: { name?: string; customer?: { fullName?: string } } | null;
  destinationWarehouse?: { name?: string } | null;
  destinationCustomer?: { fullName?: string } | null;
};

function formatMovementSiteLabel(
  site: { name?: string; customer?: { fullName?: string } } | null | undefined,
): string {
  if (!site) return "";
  return [site.customer?.fullName, site.name].filter(Boolean).join(" - ");
}

/** Resolves movement source from site, warehouse, or vendor (first match wins). */
export function getMovementSourceDisplayName(
  movement: MovementExcelLocationInput,
): string {
  const fromSite = formatMovementSiteLabel(movement.sourceSite);
  if (fromSite) return fromSite;
  const fromWarehouse = movement.sourceWarehouse?.name?.trim();
  if (fromWarehouse) return fromWarehouse;
  const fromVendor = movement.sourceVendor?.fullName?.trim();
  if (fromVendor) return fromVendor;
  return "";
}

/** Resolves movement destination from site, warehouse, or customer (first match wins). */
export function getMovementDestinationDisplayName(
  movement: MovementExcelLocationInput,
): string {
  const toSite = formatMovementSiteLabel(movement.destinationSite);
  if (toSite) return toSite;
  const toWarehouse = movement.destinationWarehouse?.name?.trim();
  if (toWarehouse) return toWarehouse;
  const toCustomer = movement.destinationCustomer?.fullName?.trim();
  if (toCustomer) return toCustomer;
  return "";
}

function normalizeActivityToken(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");
}

/**
 * Multiplier for quantities in site movement exports: +1 increases on-site net, -1 decreases, 0 neutral.
 * Prefers destination/source site ids; otherwise infers only for subtypes that always imply site flow.
 */
export function movementQuantitySignForSite(
  movement: MovementExcelSignInput,
  siteId: string,
): 1 | -1 | 0 {
  const sid = String(siteId ?? "").trim();
  if (!sid) return 1;

  const srcRaw = movement.sourceSiteId;
  const dstRaw = movement.destinationSiteId;
  const src = srcRaw != null && String(srcRaw) !== "" ? String(srcRaw).trim() : "";
  const dst = dstRaw != null && String(dstRaw) !== "" ? String(dstRaw).trim() : "";

  if (dst === sid && src !== sid) return 1;
  if (src === sid && dst !== sid) return -1;

  const typ = normalizeActivityToken(movement.activityType);
  const sub = normalizeActivityToken(movement.activitySubType);

  if (sub === "return" && typ === "inflow") return -1;
  if (sub === "deliver" && typ === "outflow") return 1;
  if (sub === "site transfer" && typ === "inflow") return 1;
  if (sub === "site transfer" && typ === "outflow") return -1;
  if (sub === "purchase" && typ === "inflow") return 1;
  if (sub === "sell" && typ === "outflow") return -1;
  if (sub === "dispose" && typ === "outflow") return -1;

  return 0;
}

/**
 * First header row of the site movement Excel: actual source/destination names per movement.
 * Inbound movements prefix the source with "FROM"; outbound use "TO" + destination when available.
 */
export function movementExcelFromLabel(
  movement: MovementExcelLocationInput,
  siteId: string,
): string {
  const sign = movementQuantitySignForSite(movement, siteId);
  const source = getMovementSourceDisplayName(movement);
  const destination = getMovementDestinationDisplayName(movement);

  if (sign === 1 && source) return `FROM ${source}`;
  if (sign === -1 && destination) return `TO ${destination}`;
  if (source) return source;
  if (destination) return sign === -1 ? `TO ${destination}` : destination;
  return movementExcelDirectionLabel(movement, siteId);
}

/**
 * Short generic header label (fallback when source/destination names are unavailable).
 */
export function movementExcelDirectionLabel(
  movement: MovementExcelSignInput,
  siteId: string,
): string {
  const sign = movementQuantitySignForSite(movement, siteId);
  const sub = normalizeActivityToken(movement.activitySubType);

  if (sign === 0) {
    if (sub === "warehouse transfer") return "WH TRANSFER";
    if (sub === "purchase") return "PURCHASE";
    return "MOVEMENT";
  }

  if (sign === 1) {
    if (sub === "deliver") return "FROM WAPO";
    if (sub === "purchase") return "FROM VENDOR";
    if (sub === "site transfer") return "TRANSFER IN";
    if (sub === "warehouse transfer") return "WH TRANSFER IN";
    return "IN";
  }

  if (sub === "return") return "TO WAPO";
  if (sub === "site transfer") return "TRANSFER OUT";
  if (sub === "dispose") return "DISPOSED";
  if (sub === "sell") return "SOLD";
  if (sub === "warehouse transfer") return "WH TRANSFER OUT";
  if (sub === "deliver") return "OUT";
  return "OUT";
}

/**
 * Human-readable message from API failures (Nest-style `{ message }` on `response.data`,
 * or `Error` instances thrown after `handleAxiosError` in `api.ts`).
 */
export function getBackendErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  const data = (error as { response?: { data?: { message?: unknown } } })?.response?.data;
  const msg = data?.message;
  if (typeof msg === "string" && msg.trim()) return msg;
  if (Array.isArray(msg)) return msg.map(String).filter(Boolean).join(", ");
  return fallback;
}